import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Sparkle, CaretUp, CaretDown } from "@phosphor-icons/react";
import { fetchAlerts, markAlertRead, markAllAlertsRead, getSettings } from "../lib/api";
import { formatPct, formatSignedPct } from "../lib/format";

const LS_SEEN_KEY = "mfsmai_seen_alert_ids";

const getSeen = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(LS_SEEN_KEY) || "[]"));
  } catch {
    return new Set();
  }
};
const saveSeen = (set) => {
  try {
    localStorage.setItem(LS_SEEN_KEY, JSON.stringify([...set].slice(-200)));
  } catch {}
};

export default function AlertsBell() {
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [notifEnabled, setNotifEnabled] = useState(false);
  const popoverRef = useRef(null);
  const navigate = useNavigate();
  const firstRunRef = useRef(true);

  const load = async () => {
    try {
      const d = await fetchAlerts(20);
      const items = d.items || [];
      setAlerts(items);
      setUnread(d.unread || 0);

      // Fire browser notifications for newly arrived alerts
      const seen = getSeen();
      const newOnes = items.filter((a) => !seen.has(a.alert_id) && !a.read);
      if (!firstRunRef.current && notifEnabled && newOnes.length > 0) {
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          newOnes.slice(0, 3).forEach((a) => {
            try {
              const n = new Notification("MFSMAI Oracle · Edge spotted", {
                body: `${a.question}\nAI ${(a.probability * 100).toFixed(0)}% vs MKT ${(a.marketYesPrice * 100).toFixed(0)}% · ${a.edge >= 0 ? "+" : ""}${(a.edge * 100).toFixed(1)}% edge`,
                tag: a.alert_id,
                icon: a.image || "/favicon.ico",
              });
              n.onclick = () => {
                window.focus();
                navigate(`/market/${a.marketId}`);
                n.close();
              };
            } catch {}
          });
        }
      }
      items.forEach((a) => seen.add(a.alert_id));
      saveSeen(seen);
      firstRunRef.current = false;
    } catch {
      setAlerts([]);
      setUnread(0);
    }
  };

  // Load settings once to know if notifications are enabled
  useEffect(() => {
    getSettings()
      .then((s) => setNotifEnabled(!!s.enable_browser_notifications))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifEnabled]);

  useEffect(() => {
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const onItemClick = async (a) => {
    if (!a.read) {
      await markAlertRead(a.alert_id);
      load();
    }
    setOpen(false);
    navigate(`/market/${a.marketId}`);
  };

  return (
    <div className="relative" ref={popoverRef}>
      <button
        data-testid="alerts-bell-btn"
        onClick={() => setOpen((o) => !o)}
        className="relative flex items-center justify-center w-9 h-9 border border-white/15 hover:border-white/40 text-zinc-300 hover:text-white transition-colors"
      >
        <Bell size={16} weight="duotone" />
        {unread > 0 && (
          <span
            data-testid="alerts-unread-badge"
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-edge text-black text-[10px] font-mono font-semibold leading-none flex items-center justify-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="alerts-popover"
          className="absolute right-0 top-full mt-2 w-[380px] max-h-[480px] overflow-y-auto bg-zinc-950 border border-white/15 z-50 shadow-2xl"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <Sparkle size={14} weight="fill" className="text-edge" />
              <span className="font-display text-xs uppercase tracking-[0.22em]">
                AI-Edge Alerts
              </span>
            </div>
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest">
              {unread > 0 && (
                <button
                  data-testid="alerts-mark-all-read"
                  onClick={async () => { await markAllAlertsRead(); load(); }}
                  className="text-zinc-400 hover:text-white"
                >
                  Mark all read
                </button>
              )}
              <Link
                to="/alerts"
                onClick={() => setOpen(false)}
                className="text-edge hover:text-white"
              >
                View all
              </Link>
            </div>
          </div>

          {alerts.length === 0 ? (
            <div className="p-6 text-center text-zinc-500 font-body text-sm">
              No alerts yet. We'll ping you when AI spots an edge.
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {alerts.map((a) => (
                <button
                  key={a.alert_id}
                  data-testid={`alert-item-${a.alert_id}`}
                  onClick={() => onItemClick(a)}
                  className={`w-full text-left p-3 hover:bg-white/5 transition-colors ${
                    !a.read ? "bg-edge/5" : ""
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {a.image && (
                      <img src={a.image} alt="" className="w-8 h-8 object-cover border border-white/10" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-body line-clamp-2">{a.question}</div>
                      <div className="mt-1.5 flex items-center gap-3 text-[11px] font-mono tabular-nums">
                        <span className="text-zinc-500">
                          MKT {formatPct(a.marketYesPrice, 0)}
                        </span>
                        <span className="text-edge">
                          AI {formatPct(a.probability, 0)}
                        </span>
                        <span
                          className={`inline-flex items-center gap-0.5 ${
                            a.edge >= 0 ? "text-profit" : "text-loss"
                          }`}
                        >
                          {a.edge >= 0 ? <CaretUp size={9} weight="fill" /> : <CaretDown size={9} weight="fill" />}
                          {formatSignedPct(a.edge, 0)}
                        </span>
                      </div>
                    </div>
                    {!a.read && <span className="mt-1 h-2 w-2 bg-edge shrink-0 rounded-full" />}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
